<script setup>
import { ref, onMounted, computed } from 'vue';
import { useFetch } from "@/plugins/fetch";
import { useToast } from 'primevue/usetoast';
import { useSensorStore } from '@/store/SensorStore';
import { useSettingStore } from '@/store/SettingsStore';
import { getUnitLabel } from '@/plugins/utils';

// PrimeVue Components
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Sidebar from 'primevue/sidebar';
import Calendar from 'primevue/calendar';
import MultiSelect from 'primevue/multiselect';
import Dropdown from 'primevue/dropdown';
import Skeleton from 'primevue/skeleton';
import ArgonButton from "@/components/Argon/ArgonButton.vue";

// HOOKS
const fetch = useFetch();
const toast = useToast();
const sensorStore = useSensorStore();
const settingsStore = useSettingStore();

// STATE
const isSidebarVisible = ref(false);
const isLoading = ref(false);
const sensorData = ref([]);

const form = ref({
  sensorIds: [],
  dataTypes: [],
  startDate: null,
  endDate: null,
  format: 'csv',
});

const downloadFormats = ref([
  { label: 'CSV (.csv)', value: 'csv' },
  { label: 'Excel (.xlsx)', value: 'xlsx' },
]);

const dataTypeOptions = ref([
  { label: 'Temperature', value: 'temperature' },
  { label: 'Humidity', value: 'humidity' },
  { label: 'Pressure', value: 'pressure' },
]);

// COMPUTED
const sensorOptions = computed(() => sensorStore.sensors.map(s => ({ name: s.name, code: s.unique_id })));
const isFetchDisabled = computed(() => !form.value.sensorIds.length || !form.value.dataTypes.length || !form.value.startDate || !form.value.endDate);

// LIFECYCLE
onMounted(() => {
  sensorStore.fetchSensors();
});

// METHODS
const fetchData = async () => {
  if (isFetchDisabled.value) return;
  isLoading.value = true;
  isSidebarVisible.value = false;
  try {
    const params = new URLSearchParams({
      sensorIds: form.value.sensorIds.join(','),
      startDate: form.value.startDate.toISOString(),
      endDate: form.value.endDate.toISOString(),
      dataTypes: form.value.dataTypes.join(','),
    });
    const response = await fetch(`sensors/data?${params}`);
    sensorData.value = response;
    if (!response.length) {
      toast.add({ severity: 'info', summary: 'No Data', detail: 'No data found for the selected criteria.', life: 4000 });
    }
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to fetch sensor data.', life: 3000 });
  } finally {
    isLoading.value = false;
  }
};

const downloadData = async () => {
  if (isFetchDisabled.value) return;
  toast.add({ severity: 'info', summary: 'Exporting', detail: 'Your data export is being prepared...', life: 2000 });
  try {
    const params = new URLSearchParams({
      sensorIds: form.value.sensorIds.join(','),
      startDate: form.value.startDate.toISOString(),
      endDate: form.value.endDate.toISOString(),
      dataTypes: form.value.dataTypes.join(','),
      format: form.value.format,
    });
    const response = await fetch(`sensors/export?${params}`);
    if (response.download_url) {
      const link = document.createElement('a');
      link.href = response.download_url;
      link.download = `sensor-data-export.${form.value.format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.add({ severity: 'success', summary: 'Success', detail: 'Data exported successfully!', life: 3000 });
    }
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to export data.', life: 3000 });
  }
};

const getSensorName = (uniqueId) => {
  return sensorStore.sensors.find(s => s.unique_id === uniqueId)?.name || uniqueId;
};

const formatValue = (row) => {
  const value = row.value; // Assuming your API provides the original value
  const isMetric = settingsStore.isMetricUnits;
  // This part needs a conversion utility if you support both metric/imperial
  // For now, we just append the unit.
  return `${value} ${getUnitLabel(row.data_type, isMetric)}`;
};

</script>

<template>
  <div class="py-4 container-fluid">
    <Toast />
    <div class="card">
      <div class="card-header border-bottom">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h6 class="mb-0">Data Exporter</h6>
            <p class="text-sm mb-0">Select and export historical sensor data.</p>
          </div>
          <ArgonButton color="primary" variant="gradient" @click="isSidebarVisible = true">
            <i class="fas fa-filter me-2"></i>
            Select Data & Filters
          </ArgonButton>
        </div>
      </div>

      <div class="card-body px-0 pt-0 pb-2">
        <DataTable :value="sensorData" :loading="isLoading" responsiveLayout="scroll" paginator :rows="10">
          <template #empty>
            <div class="text-center py-5">
              <i class="fas fa-cloud-download-alt fa-3x text-muted mb-3"></i>
              <h5 class="text-muted">Your Data Awaits</h5>
              <p>Use the filter panel to select and preview data to export.</p>
            </div>
          </template>
          <template #loading>
            <div class="p-4">
              <Skeleton v-for="i in 5" :key="i" width="100%" height="2rem" class="mb-2"></Skeleton>
            </div>
          </template>
          <Column field="sensor_id" header="Sensor Name" sortable>
            <template #body="{ data }">{{ getSensorName(data.sensor_id) }}</template>
          </Column>
          <Column field="time" header="Time" sortable></Column>
          <Column field="data_type" header="Data Type" sortable class="text-capitalize"></Column>
          <Column field="value" header="Value" sortable>
            <template #body="{ data }">{{ formatValue(data) }}</template>
          </Column>
        </DataTable>
      </div>
    </div>

    <Sidebar v-model:visible="isSidebarVisible" position="right" class="p-sidebar-md">
      <template #header>
        <h5 class="m-0">Filters & Export</h5>
      </template>
      <div class="p-fluid">
        <div class="mb-4">
          <label for="sensors" class="form-label fw-bold">1. Select Sensors</label>
          <MultiSelect id="sensors" v-model="form.sensorIds" :options="sensorOptions" optionLabel="name" optionValue="code" placeholder="Choose one or more sensors" display="chip" />
        </div>
        <div class="mb-4">
          <label for="dataTypes" class="form-label fw-bold">2. Select Data Types</label>
          <MultiSelect id="dataTypes" v-model="form.dataTypes" :options="dataTypeOptions" optionLabel="label" optionValue="value" placeholder="Choose data types" display="chip" />
        </div>
        <div class="mb-4">
          <label for="dateRange" class="form-label fw-bold">3. Select Date Range</label>
          <Calendar id="dateRange" v-model="form.startDate" placeholder="Start Date" :showTime="true" class="mb-2 w-100" />
          <Calendar id="dateRangeEnd" v-model="form.endDate" placeholder="End Date" :showTime="true" class="w-100" />
        </div>
        <div class="mb-4">
          <label for="format" class="form-label fw-bold">4. Choose Export Format</label>
          <Dropdown id="format" v-model="form.format" :options="downloadFormats" optionLabel="label" optionValue="value" />
        </div>
      </div>
      <template #footer>
        <div class="d-grid gap-2">
          <Button label="Preview Data" icon="pi pi-eye" @click="fetchData" :disabled="isFetchDisabled" />
          <Button label="Download Export" icon="pi pi-download" class="p-button-success" @click="downloadData" :disabled="!sensorData.length" />
        </div>
      </template>
    </Sidebar>
  </div>
</template>
<style>
.p-sidebar-md {
  width: 25rem;
}
.text-capitalize {
  text-transform: capitalize;
}
</style>
